import {
  Anchor,
  AppShell,
  Button,
  Group,
  Loader,
  Modal,
  Skeleton,
  Table,
  Text,
} from "@mantine/core";
import {
  CloneEmailTemplateRequest,
  DeleteEmailTemplateRequest,
  useCloneEmailTemplateMutation,
  useDeleteEmailTemplateMutation,
  useGetEmailTemplatesQuery,
} from "../slices/api/emailApi";
import { JSX, useEffect, useState } from "react";
import dayjs from "dayjs";
import { useDisclosure } from "@mantine/hooks";
import { Link, useLocation } from "wouter";
import { TextSearchBox } from "../shared/components/TextSearchBox";
import {
  displayServerErrorMessage,
  handleFetchingError,
} from "../shared/ErrorMessage";
import { openConfirmationModal } from "../shared/components/Notifications";
import {
  IconAddNew,
  IconClone,
  IconDelete,
  IconEdit,
} from "../shared/components/Icons";
import { useGetVentQuery } from "../slices/api/ventsApi";
import CreateEmailTemplate from "./CreateEmailTemplate";
import { EbTooltip } from "../shared/components/EbTooltip";
import { EbActionIcon } from "../shared/components/EbActionItem";
import { setConsoleIds } from "./Console";
import { TabHeader } from "../shared/components/TabHeader";

interface Props {
  ventId: string;
  setEventTabDescription: (name: string) => void;
}
function EmailTemplatesTab(props: Props) {
  const [_, navigate] = useLocation();
  const [searchText, setSearchText] = useState("");

  const [opened, { open, close }] = useDisclosure(false);
  const [deleteEmailTemplate] = useDeleteEmailTemplateMutation();
  const [cloneEmailTemplate] = useCloneEmailTemplateMutation();
  const {
    data: getEmailTemplatesResponse,
    isFetching: isFetchingGetEmailTemplates,
    isSuccess: isSuccessGetEmailTemplates,
  } = useGetEmailTemplatesQuery({ ventId: props.ventId });
  const {
    data: ventResponse,
    isError: isErrorFetchingVent,
    error: ventError,
  } = useGetVentQuery({
    id: props.ventId,
  });
  useEffect(() => {
    if (ventResponse) {
      props.setEventTabDescription(ventResponse.name);
    }
  }, [ventResponse]);

  const handleDelete = async (emailTemplateId: string) => {
    if (emailTemplateId === undefined) {
      console.error("emailTemplateId undefined");
      return;
    }

    const request: DeleteEmailTemplateRequest = {
      id: emailTemplateId,
    };

    try {
      await deleteEmailTemplate(request).unwrap();
      setConsoleIds({ emailTemplateId: null });
    } catch (error) {
      displayServerErrorMessage(error);
    }
  };

  const handleClone = async (emailTemplateId: string) => {
    if (emailTemplateId === undefined) {
      console.error("emailTemplateId undefined");
      return;
    }

    const request: CloneEmailTemplateRequest = {
      emailTemplateId: emailTemplateId,
    };

    try {
      const r = await cloneEmailTemplate(request).unwrap();
      navigate(
        `/console/emailTemplate/event/${r.ventId}/emailTemplate/${r.emailTemplateId}`
      );
    } catch (error) {
      displayServerErrorMessage(error);
    }
  };

  if (isErrorFetchingVent) {
    handleFetchingError(ventError);
    return null;
  }

  if (!getEmailTemplatesResponse || !ventResponse) {
    return (
      <AppShell.Main>
        <Loader color="blue" />
      </AppShell.Main>
    );
  }

  let rows: JSX.Element[] = [];
  if (
    isSuccessGetEmailTemplates &&
    getEmailTemplatesResponse.emailTemplates &&
    getEmailTemplatesResponse.emailTemplates.length > 0
  ) {
    const filteredEmailTemplates =
      getEmailTemplatesResponse.emailTemplates.filter((emailTemplate) =>
        emailTemplate.name.toLowerCase().includes(searchText.toLowerCase())
      );
    const sorted = filteredEmailTemplates.sort((a, b) =>
      a.updateTime < b.updateTime ? 1 : -1
    );
    rows = sorted.map((emailTemplate) => (
      <Table.Tr key={emailTemplate.id}>
        <Table.Td>
          <Group gap="0.5rem">
            <EbActionIcon
              aria-label="Configure email template"
              onClick={() => {
                navigate(
                  `/console/emailTemplate/event/${props.ventId}/emailTemplate/${emailTemplate.id}`
                );
              }}
            >
              <EbTooltip label="Configure email template">
                <IconEdit />
              </EbTooltip>
            </EbActionIcon>
            <EbActionIcon
              disabled={!ventResponse.canUserEdit}
              aria-label="Clone"
              onClick={() => {
                handleClone(emailTemplate.id);
              }}
            >
              <EbTooltip label="Clone">
                <IconClone />
              </EbTooltip>
            </EbActionIcon>
            <EbActionIcon
              disabled={!ventResponse.canUserEdit}
              aria-label="Delete email template"
              onClick={() => {
                openConfirmationModal({
                  message: `Please confirm deletion of the template ${emailTemplate.name}`,
                  title: "Delete Email Template",
                  onConfirm: () => handleDelete(emailTemplate.id),
                });
              }}
            >
              <EbTooltip label="Delete email template">
                <IconDelete />
              </EbTooltip>
            </EbActionIcon>
          </Group>
        </Table.Td>
        <Table.Td>{emailTemplate.name}</Table.Td>
        <Table.Td>{emailTemplate.subject}</Table.Td>
        <Table.Td>
          {emailTemplate.nextReminderTime === ""
            ? ""
            : dayjs(emailTemplate.nextReminderTime).format("LLL z")}
        </Table.Td>
        <Table.Td>{dayjs(emailTemplate.updateTime).format("LLL z")}</Table.Td>
      </Table.Tr>
    ));
  }
  return (
    <AppShell.Main>
      <Modal opened={opened} onClose={close} withCloseButton={false} centered>
        <CreateEmailTemplate ventId={props.ventId} onClose={close} />
      </Modal>
      <TabHeader
        label="Email Templates for"
        ventId={ventResponse.id}
        ventName={ventResponse.name}
      />
      <Text size="sm">
        To send emails click{" "}
        <Anchor
          component={Link}
          href={`/console/guests/event/${props.ventId}`}
          underline="hover"
        >
          here
        </Anchor>{" "}
        and select guests
      </Text>
      <Button onClick={open} rightSection={<IconAddNew />} mt="md">
        Add Email Template
      </Button>
      <TextSearchBox setSearchText={setSearchText} searchText={searchText} />
      <Skeleton visible={isFetchingGetEmailTemplates}>
        <div hidden={isSuccessGetEmailTemplates === false || rows.length === 0}>
          <Table stickyHeader stickyHeaderOffset={0}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Actions</Table.Th>
                <Table.Th>Template name</Table.Th>
                <Table.Th>Subject</Table.Th>
                <Table.Th>Next Auto Reminder</Table.Th>
                <Table.Th>Updated</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
        </div>
        <div hidden={isFetchingGetEmailTemplates || rows.length > 0}>
          No email templates displayed. Change search or add an email template.
        </div>
      </Skeleton>
    </AppShell.Main>
  );
}

export default EmailTemplatesTab;
